import React, { useCallback, useState } from 'react';
import type { FingeringStorageResult } from '../../../utils/fingeringStorage';
import { useGoogleDriveSaver } from '../../../hooks/useGoogleDriveSaver';
import GoogleDriveIcon from '../../../components/icons/GoogleDriveIcon';
import { APP_FOLDER_NAME } from '../../../utils/driveApi';
import type { DriveTargetFolder } from '../../../utils/driveTargetFolder';

interface DriveSaveButtonProps {
  stored: FingeringStorageResult | null;
  title: string;
  part: string;
  /** 保存先フォルダ。SSOT は FingeringPage の driveTarget state。null は既定 APP_FOLDER 行き。 */
  target: DriveTargetFolder | null;
  /** issue #142: 手動保存が成功した瞬間に呼ぶ。 親が共有リンクコピーボタンを活性化する。 */
  onSaved?: () => void;
}

const DriveSaveButton: React.FC<DriveSaveButtonProps> = ({ stored, title, part, target, onSaved }) => {
  const { isConfigured, isSaving, error, saveFingering } = useGoogleDriveSaver();
  const [notice, setNotice] = useState<string | null>(null);

  const canSave =
    isConfigured &&
    !!stored &&
    !!stored.fingeringJson &&
    !!stored.originalPdfBlob &&
    !isSaving;

  const handleSave = useCallback(async () => {
    if (!stored) return;
    setNotice(null);
    try {
      const id = await saveFingering(stored, {
        title,
        part,
        // issue #107: mode 欠落 (旧データ / 再生成穴) でも 'part' フォールバックで保存する。
        // 復元側 driveZipArchive と整合し、Drive 保存ボタンを常に有効化する。
        mode: stored.mode ?? 'part',
        parentFolderId: target?.id,
      });
      setNotice(`Drive に保存しました (fileId: ${id.slice(0, 8)}…)`);
      onSaved?.();
    } catch {
      // error state is surfaced via the hook
    }
  }, [saveFingering, stored, title, part, target, onSaved]);

  const folderLabel = target?.name ?? APP_FOLDER_NAME;

  return (
    <div data-testid="drive-save-button-wrapper" className="w-full">
      <button
        type="button"
        onClick={handleSave}
        disabled={!canSave}
        aria-label="Drive に保存"
        className="w-full group flex items-center gap-3 p-3 sm:p-3.5 bg-gradient-to-r from-sky-50 to-blue-50 border border-blue-200/60 rounded-xl hover:border-blue-300 hover:shadow-md hover:shadow-blue-100 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
      >
        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-blue-100">
          <GoogleDriveIcon className="w-5 h-5" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-700">
            {isSaving ? 'Drive に保存中…' : 'Drive に保存'}
          </div>
          <div className="text-xs text-gray-400 truncate">
            保存先: <span className="font-medium">{folderLabel}</span>
          </div>
        </div>
        <svg
          className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m-7-7l7 7-7 7" />
        </svg>
      </button>
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-brand-red px-2">
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="mt-1.5 text-xs text-gray-500 px-2">{notice}</p>
      )}
    </div>
  );
};

export default DriveSaveButton;
